import { Collapse } from "@mui/material";
import { Avatar } from "@mui/material";
import Plus from "assets/img/svg/plus";
import ButtonCollapse from "components/shared/ButtonCollapse";
import CustomFieldsCollapse from "components/shared/CustomFieldsCollapse";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import TextAreaFormComponent from "components/shared/FormComponent/TextAreaFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import { onHighlightField } from "components/TitleFormContainer/utils";
import { useTranslation } from "react-i18next";
import type { ICustomField } from "store/customFieldsStore";

import type { TInitialHighlightField, TOnCollapseFields } from "../types";

interface IOtherInformationsStep {
    /**
     * React useForm() hooks
     */
    useFormData: any;
    stepTitle: string;
    stepSubtitle: string;
    indexStep: number;
    /**
     * List of tags for autocomplete field
     */
    tagList: any;
    /**
     * List of analytical codes for autocomplete field
     */
    codeNames: any;
    /**
     * List of brands for autocomplete field
     */
    brands: any;
    /**
     * Open "specifications" or "administrative informations" fields
     */
    onCollapseFields: TOnCollapseFields;
    mode: "new" | "edit-page";
    /**
     * If company has multiple brands to show the field or no
     */
    usesMultipleBrand: any;
    /**
     * Highlight fields condition after Dilicom autocomplete
     */
    highlightField: TInitialHighlightField;
    /**
     * Reset autocomplete highlight
     */
    onResetHighlight: (fieldName: string) => void;
    /**
     * Collapse fields condition
     */
    showFields: (category: "specifications" | "adminInfo") => void;
    /**
     * Name of the company for the brand autocomplete
     */
    companyName: string | undefined;
    customFields: ICustomField[];
    isBookPortal?: boolean;
}

const OtherInformationsStep = ({
    useFormData,
    stepTitle,
    stepSubtitle,
    indexStep,
    tagList,
    codeNames,
    brands,
    onCollapseFields,
    mode,
    usesMultipleBrand,
    highlightField,
    onResetHighlight,
    showFields,
    companyName,
    customFields,
    isBookPortal,
}: IOtherInformationsStep) => {
    const [t] = useTranslation();
    const { control, errors } = useFormData;

    const renderAnalysisCodeOption = (props: any, option: any) => {
        return option.addElement ? (
            <li {...props} key={option.id} className="autocomplete-new-element">
                <div className="content">
                    <Plus />
                    {option.addElement}
                </div>
            </li>
        ) : (
            <li {...props} key={option.id}>
                <span>{option ?? ""}</span>
            </li>
        );
    };
    const renderBrandsOption = (props: any, option: any) => {
        return (
            <li {...props}>
                <Avatar
                    variant="rounded"
                    src={option?.avatarUrl}
                    sx={{
                        width: "24px",
                        height: "24px",
                        backgroundColor: "#FFF2F1",
                        color: "black",
                        fontSize: "12px",
                    }}
                >
                    {option.name?.slice(0, 1).toUpperCase()}
                </Avatar>
                <div className="separator" />
                <span>{companyName && option.name === "Par défaut" ? companyName : option.name}</span>
            </li>
        );
    };

    return (
        <>
            <h3 className="form-section-title">
                {" "}
                {indexStep + 1}. {stepTitle}{" "}
            </h3>
            <span className="form-section-description">{stepSubtitle}</span>
            <div className="form-fields">
                {/* SPECIFICATIONS FIELDS */}
                <ButtonCollapse
                    btnText={t("Titles.Specification")}
                    collapse={onCollapseFields.specifications}
                    onCollapse={() => showFields("specifications")}
                />
                <Collapse in={onCollapseFields.specifications} sx={collapseStyle} timeout="auto" unmountOnExit>
                    <TextFormComponent
                        label={t("NewTitle.CumulativeDraw")}
                        placeholder={t("NewTitle.CumulativeDraw")}
                        control={control}
                        controlName="draw"
                        error={errors.draw}
                    />
                    <TextFormComponent
                        label={t("NewTitle.LiteraryPrize")}
                        placeholder={t("NewTitle.LiteraryPrize")}
                        control={control}
                        controlName="prize"
                        error={errors.prize}
                    />
                    <TextFormComponent
                        label={t("Titles.Collection")}
                        placeholder={t("Titles.Collection")}
                        control={control}
                        controlName="category"
                        sx={onHighlightField(highlightField.category)}
                        onFocus={() => onResetHighlight("category")}
                        error={errors.category}
                    />
                    <TextFormComponent
                        label={t(isBookPortal ? "NewTitle.FormatInformation" : "NewTitle.Format")}
                        placeholder={t(isBookPortal ? "NewTitle.FormatInformation" : "NewTitle.Format")}
                        control={control}
                        controlName="format"
                        sx={onHighlightField(highlightField.format)}
                        onFocus={() => onResetHighlight("format")}
                        error={errors.format}
                    />
                    <TextFormComponent
                        label={t("NewTitle.PageNumber")}
                        placeholder={t("NewTitle.PageNumber")}
                        control={control}
                        controlName="pagination"
                        sx={onHighlightField(highlightField.pagination)}
                        onFocus={() => onResetHighlight("pagination")}
                        error={errors.pagination}
                    />
                </Collapse>

                {/* ADMINISTRATIVE INFO FIELDS*/}
                <ButtonCollapse
                    btnText={t("NewTitle.AdminInfo")}
                    onCollapse={() => showFields("adminInfo")}
                    collapse={onCollapseFields.adminInfo}
                    sx={{ marginTop: onCollapseFields.specifications ? "40px" : "0px" }}
                />
                <Collapse in={onCollapseFields.adminInfo} sx={collapseStyle} timeout="auto" unmountOnExit>
                    <TextFormComponent
                        label={t("Titles.DistributorReference")}
                        placeholder={t("Titles.DistributorReference")}
                        control={control}
                        controlName="distributorReference"
                        error={errors.DistributorReference}
                    />
                    {!isBookPortal && (
                        <AutocompleteFormComponent
                            label={t("ImportData.TitleAnalysisCode")}
                            control={control}
                            controlName="analysisCode"
                            options={codeNames || []}
                            renderOptionText={(option: any) => `${option}`}
                            addElement={t("AnalyticalCode.AddAnalyticalCode")}
                            renderOption={renderAnalysisCodeOption}
                            defaultFilterOptions
                        />
                    )}
                    {!isBookPortal && usesMultipleBrand && (
                        <AutocompleteFormComponent
                            label={t("NewPublication.Brand")}
                            control={control}
                            controlName="associatedBrand"
                            options={brands}
                            renderOptionText={(option: any) =>
                                companyName && option.name === "Par défaut" ? companyName : option.name
                            }
                            renderOption={renderBrandsOption}
                            error={errors.associatedBrand}
                            defaultFilterOptions
                        />
                    )}
                    <TextFormComponent
                        label={t("NewTitle.ShortNote")}
                        placeholder={t("NewTitle.ShortNote")}
                        control={control}
                        controlName="shortNote"
                        error={errors.shortNote}
                    />
                </Collapse>

                {/* SUMMARY FIELD */}
                <TextAreaFormComponent
                    label={t("NewTitle.Summary")}
                    control={control}
                    controlName="summary"
                    minRows={4}
                    maxRows={12}
                    style={onHighlightTextArea(highlightField.summary)}
                    onFocus={() => onResetHighlight("summary")}
                />
                <TextAreaFormComponent
                    label={t("NewTitle.Notes")}
                    control={control}
                    controlName="notes"
                    minRows={4}
                    maxRows={12}
                />

                {/** COLLAPSE FOR THE CUSTOM FIELDS */}
                {customFields.length > 0 && (
                    <CustomFieldsCollapse sectionFields={customFields} errors={errors} mode={mode} />
                )}
            </div>
        </>
    );
};

export default OtherInformationsStep;

const onHighlightTextArea = (highlight: boolean) => ({
    backgroundColor: highlight ? "#EEF3FD" : "rgb(238 243 253 / 0%)",
    border: highlight ? "1px solid #275AB0" : "1px solid #EAEAEA",
});

const collapseStyle = {
    ".MuiCollapse-wrapperInner": {
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
    },
};
